import { LocalCaseStorage } from "@dandelion/core/storages";
import {
  CaseMetaData,
  HistoryPartialStoredCase, MagicNumbers,
  PartialStoredCase,
  SaveMetaData,
  StorageError,
  StorageErrorType, Stringifier
} from "@dandelion/core";
import _ from "lodash";

class LocalCaseDTO {
  protected readonly serializedData: string;

  constructor(readonly caseMetaData: SaveMetaData, readonly partialStoredCases: Array<PartialStoredCase>) {
    this.serializedData = new Stringifier().serialize(_.cloneDeep({ caseMetaData, partialStoredCases }));
  }

  public static from(caseMetaData: SaveMetaData, partialStoredCases: Array<PartialStoredCase>): string {
    const instance = new LocalCaseDTO(caseMetaData, partialStoredCases);
    return instance.serializedData;
  }
}

export class LocalCaseStorageForProsafe extends LocalCaseStorage {

  async getAllCaseMetaData(caseType: string): Promise<CaseMetaData[]> {
    const result: CaseMetaData[] = [];
    for (let key in localStorage) {
      if (key.startsWith('partials-')) {
        const raw = localStorage.getItem(key)!;
        const partials = new Stringifier<{
          partialStoredCases: PartialStoredCase[];
          caseMetaData: CaseMetaData;
        }>().deserialize(raw);
        //TODO fix types if possible
        const newMap = new Map<string, Map<string, any>>();
        partials.caseMetaData.status.forEach((value, key) => {
          if (!newMap.has("*")) {
            newMap.set("*", new Map());
          }
          newMap.get("*")?.set(key, value);
        });
        if (caseType == partials.caseMetaData.caseType) {
          result.push({ ...partials.caseMetaData, status: newMap });
        }
      }
    }
    return Promise.resolve(result);
  }

  async getCaseMetaData(caseID: string): Promise<CaseMetaData> {
    const caseMetaData = (await this.deserializeCase(caseID)).caseMetaData;
    if (!caseMetaData) {
      throw new StorageError(`Case: ${caseID} has empty caseMetaData`, StorageErrorType.CASE_METADATA_NOT_FOUND);
    }
    //TODO fix types if possible
    const newMap = new Map<string, Map<string, any>>();
    caseMetaData.status.forEach((value, key) => {
      if (!newMap.has("*")) {
        newMap.set("*", new Map());
      }
      newMap.get("*")?.set(key, value);
    });

    return { ...caseMetaData, status: newMap };
  }

  protected async updateLockStatus(caseID: string, toLock: boolean): Promise<void> {
    try {
      const partials = await this.getPartials(caseID);
      const partialsToBeSaved = await this.deserializePartials(partials);
      const caseMetaData = await this.getCaseMetaData(caseID);
      const lockDuration = MagicNumbers.localStorageLockExpiresMinutes * MagicNumbers.minuteInMS;
      const updatedCaseMetaData: CaseMetaData = {
        ...caseMetaData,
        isLocked: toLock,
        lockUser: toLock ? this.userService.getCurrentUser()?.value?.username || '' : '',
        lockExpires: toLock ? new Date(Date.now() + lockDuration) : new Date(0),
      };
      const partialStoredCasesKey = `${this.baseKey}partials-${caseID}`;
      //TODO fix types if possible
      const newMap = new Map<string, number>();
      if (updatedCaseMetaData.status instanceof Map && updatedCaseMetaData.status.has("*")) {
        const nestedMap = updatedCaseMetaData.status.get("*");
        if (nestedMap instanceof Map) {
          nestedMap.forEach((value, key) => {
            newMap.set(key, value);
          });
        }
      }
      //TODO fix types if possible
      this.localStorage.setItem(partialStoredCasesKey, LocalCaseDTO.from({ ...updatedCaseMetaData, status: newMap as any }, partialsToBeSaved));
    } catch (e) {
      const errorType = toLock ? StorageErrorType.LOCK_FAILED : StorageErrorType.UNLOCK_FAILED;
      throw new StorageError(
        `${toLock ? 'Locking' : 'Unlocking'} case ${caseID.substring(
          0,
          MagicNumbers.lenghtOfAbbreviatedCaseID
        )} failed in local storage`,
        errorType,
        e
      );
    }
  }

  protected async updateFreezeStatus(caseID: string, frozenAccessLevels: string[], toFreeze: boolean): Promise<void> {
    try {
      const partials = await this.getPartials(caseID);
      const partialsToBeSaved = await this.deserializePartials(partials);
      const caseMetaData = await this.getCaseMetaData(caseID);
      frozenAccessLevels.forEach((accessLevel) => {
        caseMetaData.frozenAccessLevels.set(accessLevel, toFreeze);
      });
      const partialStoredCasesKey = `${this.baseKey}partials-${caseID}`;
      //TODO fix types if possible
      const newMap = new Map<string, number>();
      if (caseMetaData.status instanceof Map && caseMetaData.status.has("*")) {
        const nestedMap = caseMetaData.status.get("*");
        if (nestedMap instanceof Map) {
          nestedMap.forEach((value, key) => {
            newMap.set(key, value);
          });
        }
        //TODO fix types if possible
        this.localStorage.setItem(partialStoredCasesKey, LocalCaseDTO.from({ ...caseMetaData, status: newMap as any }, partialsToBeSaved));
      }
    } catch (e) {
      const errorType = toFreeze ? StorageErrorType.FREEZE_FAILED : StorageErrorType.UNFREEZE_FAILED;
      throw new StorageError(
        `${toFreeze ? 'Freezing' : 'Unfreezing'} case ${caseID.substring(
          0,
          MagicNumbers.lenghtOfAbbreviatedCaseID
        )} failed in local storage`,
        errorType,
        e
      );
    }
  }

  async updatePartials(
    caseID: string,
    partialStoredCases: Array<PartialStoredCase>,
    saveMetaData: SaveMetaData
  ): Promise<void> {
    try {
      const partialsToBeSaved = await this.deserializePartials(partialStoredCases);
      const partialStoredCasesKey = `${this.baseKey}partials-${caseID}`;
      const history: Map<string, HistoryPartialStoredCase[]> = (await this.getHistory()) ?? new Map();
      const oldPartialsToBeSaved = await this.deserializePartials(await this.getPartials(caseID));
      const oldCaseMetaData = await this.getCaseMetaData(caseID);
      const historyCase: HistoryPartialStoredCase = {
        creationDate: new Date().toISOString(),
        case_: oldPartialsToBeSaved,
        caseMetaData_: oldCaseMetaData,
      };
      const historyCases = history.get(partialStoredCasesKey) || [];
      historyCases.push(historyCase);
      history.set(partialStoredCasesKey, historyCases);

      this.localStorage.setItem(`cases-history`, new Stringifier().serialize(_.cloneDeep(history)));
      //TODO fix types if possible
      const newMap = new Map<string, number>();
      if (saveMetaData.status instanceof Map && saveMetaData.status.has("*")) {
        const nestedMap = saveMetaData.status.get("*");
        if (nestedMap instanceof Map) {
          nestedMap.forEach((value, key) => {
            newMap.set(key, value);
          });
        }
      }

      this.localStorage.setItem(
        partialStoredCasesKey,
        //TODO fix types if possible
        LocalCaseDTO.from({ ...oldCaseMetaData, ...saveMetaData, status: newMap as any }, partialsToBeSaved)
      );
    } catch (e) {
      throw new StorageError(
        `Update partials failed for case ${caseID.substring(
          0,
          MagicNumbers.lenghtOfAbbreviatedCaseID
        )} in local storage`,
        StorageErrorType.UPDATE_CASE_FAILED,
        e
      );
    }
  }

  async createCase(partialStoredCases: Array<PartialStoredCase>, caseMetaData: CaseMetaData): Promise<void> {
    try {
      const partialStoredCasesKey = `${this.baseKey}partials-${caseMetaData.caseID}`;
      const partialsToBeSaved = await this.deserializePartials(partialStoredCases);
      //TODO fix types if possible
      const newMap = new Map<string, number>();
      if (caseMetaData.status instanceof Map && caseMetaData.status.has("*")) {
        const nestedMap = caseMetaData.status.get("*");
        if (nestedMap instanceof Map) {
          nestedMap.forEach((value, key) => {
            newMap.set(key, value);
          });
        }
      }
      //TODO fix types if possible
      this.localStorage.setItem(partialStoredCasesKey, LocalCaseDTO.from({ ...caseMetaData, status: newMap as any }, partialsToBeSaved));
    } catch (e) {
      throw new StorageError(
        `Create case ${caseMetaData.caseID.substring(
          0,
          MagicNumbers.lenghtOfAbbreviatedCaseID
        )} failed in local storage`,
        StorageErrorType.CREATE_CASE_FAILED,
        e
      );
    }
  }
}
